import { isStudyPartCohortKey } from './studyPartsNaming';

export const isEmptyStudyPartNote = (v) =>
  v == null || (typeof v === 'string' && (v.trim() === '' || v.trim() === 'Not Provided'));

/** Merge additional_notes and comments without duplicating identical text. */
export const mergeStudyPartAdditionalNotes = (...values) => {
  const seen = new Set();
  const chunks = [];
  values.forEach((v) => {
    const t = v != null ? String(v).trim() : '';
    if (!t || t === 'Not Provided' || seen.has(t)) return;
    seen.add(t);
    chunks.push(t);
  });
  return chunks.join('\n\n');
};

export const getMergedNotesForCohort = (studyParts, partKey, cohortKey) => {
  const part = studyParts?.[partKey] || {};
  return mergeStudyPartAdditionalNotes(
    part[cohortKey]?.additional_notes,
    part.direct?.comments
  );
};

export const getFullMergedNotesForCohort = (studyParts, partKey, cohortKey, studyOverviewComments) => {
  const part = studyParts?.[partKey] || {};
  return mergeStudyPartAdditionalNotes(
    studyOverviewComments,
    part[cohortKey]?.additional_notes,
    part.direct?.comments
  );
};

export const getMergedNotesForPartDirect = (studyParts, partKey, extraComments) => {
  const part = studyParts?.[partKey] || {};
  const cohortNotes = Object.keys(part)
    .filter((k) => isStudyPartCohortKey(k) && k !== 'direct')
    .map((k) => part[k]?.additional_notes)
    .filter((n) => !isEmptyStudyPartNote(n));
  return mergeStudyPartAdditionalNotes(part.direct?.comments, ...cohortNotes, extraComments);
};

export const applyMergedNotesToCohort = (form, partKey, cohortKey, mergedText) => {
  form.setFieldValue(
    `study_overview.study_parts.${partKey}.${cohortKey}.additional_notes`,
    mergedText
  );
  form.setFieldValue(`study_overview.study_parts.${partKey}.direct.comments`, '');
};

export const applyFullMergedNotesToCohort = (form, partKey, cohortKey, mergedText) => {
  form.setFieldValue(
    `study_overview.study_parts.${partKey}.${cohortKey}.additional_notes`,
    mergedText
  );
  form.setFieldValue(`study_overview.study_parts.${partKey}.direct.comments`, '');
  form.setFieldValue('study_overview.comments', '');
};

export const applyMergedNotesToPartDirect = (form, partKey, mergedText, extraFieldPath) => {
  form.setFieldValue(`study_overview.study_parts.${partKey}.direct.comments`, mergedText);
  const part = form.values?.study_overview?.study_parts?.[partKey] || {};
  Object.keys(part)
    .filter((k) => isStudyPartCohortKey(k) && k !== 'direct')
    .forEach((cohortKey) => {
      form.setFieldValue(
        `study_overview.study_parts.${partKey}.${cohortKey}.additional_notes`,
        ''
      );
    });
  if (extraFieldPath) {
    form.setFieldValue(extraFieldPath, '');
  }
};

export const formatTreatmentAdditionalInformation = (additionalInfo) => {
  if (Array.isArray(additionalInfo)) {
    return additionalInfo
      .map((s) => String(s).trim())
      .filter((s) => s && s !== 'Not Provided')
      .join('\n\n');
  }
  const text = additionalInfo != null ? String(additionalInfo).trim() : '';
  return text && text !== 'Not Provided' ? text : '';
};

export const parseTreatmentAdditionalInformation = (text) => {
  const trimmed = String(text || '').trim();
  if (!trimmed) return [];
  return trimmed
    .split(/\n\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
};

/**
 * Live-edit variants of format/parseTreatmentAdditionalInformation, used only for the
 * merged textarea's controlled value/onChange while the user is actively typing. The
 * trimming versions above re-trim every paragraph on every keystroke (including the one
 * the user is currently in), which strips a just-typed trailing space back off before the
 * next render — the box then looks like it "won't let you type a space". These preserve
 * whatever the user actually typed; run the trimming versions once elsewhere (e.g. on blur
 * or before consuming the value) if paragraph cleanup is still needed.
 */
export const splitTreatmentAdditionalInformationLive = (text) => {
  const s = String(text ?? '');
  return s ? s.split(/\n\n+/) : [];
};

export const joinTreatmentAdditionalInformationLive = (additionalInfo) => {
  if (Array.isArray(additionalInfo)) {
    return additionalInfo.map((s) => String(s ?? '')).join('\n\n');
  }
  return String(additionalInfo ?? '');
};

/** For section generation: fold part-level comments into additional_notes per cohort. */
export const mergeStudyPartsNotesForGeneration = (studyParts, studyOverviewComments) => {
  if (!studyParts || typeof studyParts !== 'object') return studyParts;
  const merged = JSON.parse(JSON.stringify(studyParts));
  Object.keys(merged).forEach((partKey) => {
    const part = merged[partKey];
    if (!part || typeof part !== 'object') return;

    const partComments = part.direct?.comments;

    Object.keys(part)
      .filter((k) => isStudyPartCohortKey(k))
      .forEach((cohortKey) => {
        const cohort = part[cohortKey];
        if (!cohort || typeof cohort !== 'object') return;
        const folded = mergeStudyPartAdditionalNotes(
          studyOverviewComments,
          cohort.additional_notes,
          partComments
        );
        if (folded) {
          cohort.additional_notes = folded;
        }
      });

    if (part.direct && partComments !== undefined) {
      const foldedDirect = mergeStudyPartAdditionalNotes(
        studyOverviewComments,
        part.direct.additional_notes,
        partComments
      );
      if (foldedDirect) {
        part.direct.additional_notes = foldedDirect;
      }
      delete part.direct.comments;
    }
  });
  return merged;
};
